// moleRemovalData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const moleRemoval: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Mole Removal Dubai | Elite Body Home Polyclinic",
            "description": "Get expert mole removal in Dubai with advanced, safe treatments. Achieve smooth, blemish-free skin under the care of our certified dermatologists.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/mole-removal-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Mole Removal Dubai | Elite Body Home Polyclinic",
                "description": "Get expert mole removal in Dubai with advanced, safe treatments. Achieve smooth, blemish-free skin under the care of our certified dermatologists.",
                "url": "https://elitebodyhome.com/services/mole-removal-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-02-13T14:23:21+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dgffhtf-scaled-large.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dgffhtf-scaled-large.jpg",
                "image_width": 1024,
                "image_height": 572,
                "image_alt": "mole removal",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Mole Removal Dubai | Elite Body Home Polyclinic",
                "description": "Get expert mole removal in Dubai with advanced, safe treatments. Achieve smooth, blemish-free skin under the care of our certified dermatologists.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dgffhtf-scaled-large.jpg"
            },
        },
        "breadcrumbs": ["Home", "Services", "Mole Removal Dubai"],
        "hero": {
            "title": "Mole Removal Dubai",
            "subtitle": "Flawless Skin with Expert Mole Removal",
            "description": "Moles are a typical kind of skin growth that can develop in different designs, sizes, and also colors. Many moles are benign and harmless, but some people choose to remove them if they are unsightly or irritating. Mole removal is also recommended in cases of suspect skin cancer, or changes in the mole's character. Whatever the reason, mole removal is generally simple and can provide not just cosmetic but peace of mind as well."
        },
        "howItWorks": {
            "title": "Procedure",
            "description": "Mole removal is a safe procedure performed to remove moles that are unwanted (for cosmetic reasons) or any suspicious moles (for medical reasons)."
        },
        "procedureSteps": {
            "heading": "Here's how it works:",
            "para": "",
            "steps": [
                {
                    "title": "Consultation & Examination",
                    "desc": "A dermatologist examines the mole to see if it needs to be cut out because of cosmetic concerns or health risks, like skin cancer.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Local Anesthesia",
                    "desc": "A local anesthetic is used to numb the area surrounding the mole, ensuring that the procedure is painless.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Excision or Shaving",
                    "desc": "The method of excision or shaving will depend on the type, size, and depth of the mole.",
                    "icon": "fa-solid fa-cut"
                },
                {
                    "title": "Cauterization & Closure",
                    "desc": "The tissue is cauterized or closed with stitches to allow it to heal without increasing the chance of infection or scarring.",
                    "icon": "fa-solid fa-burn"
                },
                {
                    "title": "Follow-Up & Aftercare",
                    "desc": "The dermatologist will give instructions about wound aftercare, which can include keeping the area clean, avoiding exposure to sunlight and applying medications as recommended.",
                    "icon": "fa-solid fa-bandage"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Having moles removed can also serve you aesthetically or medically—this is relative to why you need to have it removed. Here's a closer look at how this surgery can be beneficial:",
            "items": [
                {
                    "title": "Cosmetic Enhancement",
                    "desc": "Stimulates deeper collagen renewal to increase skin elasticity and smoothness.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Prevention of Skin Irritation",
                    "desc": "Moles in areas that rub between clothes and jewelry can induce dermatitis. This helps avoid the problem of removal.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "Skin Cancer Early Detection",
                    "desc": "Laboratory tests can identify early indications of skin cancer, such as melanoma, from removed suspicious moles.",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "Boost in self-confidence",
                    "desc": "Large or multiple moles can also have aesthetic impact which can affect self-esteem. Getting rid of them may boost one's confidence and satisfaction in their looks.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Not everyone is a proper candidate for mole removal. The first step in the progress is to find out if you are a candidate for mole removal.",
            "items": [
                {
                    "title": "People with abnormal moles",
                    "desc": "A mole that has irregular borders, changes sizes, or has different colors — you need to see a dermatologist. These could be any early indications of skin cancer, and the sooner they're removed and tested, the better.",
                    "icon": "fa-solid fa-circle-exclamation"
                },
                {
                    "title": "People who are irritated or uncomfortable",
                    "desc": "Moles that interfere with daily routines, such as when you're shaving, or that become inflamed from being rubbed so often, can be safely excised.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "Cosmetic candidates",
                    "desc": "If you are unhappy with how a mole looks for aesthetic reasons, you may wish to have it removed.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "People who are in stable health",
                    "desc": "If you do not have underlying health conditions that could hinder your healing process, it is likely safe for you to have the procedure.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "The more you know how to prepare for your mole treatment, the smoother the process will be, and the better the results will be.",
            "items": [
                {
                    "title": "Visit a dermatologist",
                    "desc": "The first step when considering mole removal is to have a consultation with an experienced dermatologist; This will help guide the best removal procedure relative to the mole's characteristics and your skin type.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Avoiding sun exposure",
                    "desc": "Moles are harder to remove and heal from if the skin is sun-damaged. In the days prior to the procedure, it's important to protect the skin by not exposing it to direct sunlight.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Discontinue blood-thinning medications",
                    "desc": "People taking any blood thinners, including aspirin, may be instructed by their doctor to stop them temporarily to lower risk of excessive bleeding during the procedure.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Be informed about aftercare",
                    "desc": "Abiding by post-removal care instructions for the treated area can also help avoid possible infections or complications and promote healing.",
                    "icon": "fa-solid fa-book-medical"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Results of mole removal are usually permanent, particularly if the mole has been completely excised. While patients will have smooth, mole-free skin, some light scarring is possible.",
            "items": [
                {
                    "title": "Immediate results",
                    "desc": "The mole will not be visible once the procedure is done. If stitches were used, these will be removed after one to two weeks.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "Scarring",
                    "desc": "Although scarring can occur, particularly with excision, this usually lightens with time. You can apply creams that are meant to reduce scars and make sure the area stays moisturized.",
                    "icon": "fa-solid fa-scissors"
                },
                {
                    "title": "Follow-up",
                    "desc": "For concerning moles, the tissue is sent to a laboratory for biopsy. If any abnormal cells are found, your doctor will advise the next steps in treatment.",
                    "icon": "fa-solid fa-microscope"
                }
            ]
        },

        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Is mole removal painful?",
                    "answer": "No. Mole removal is typically performed under local anesthesia, ensuring minimal discomfort during the procedure. Post-procedure pain is usually mild and can be managed with over-the-counter pain relief."
                },
                {
                    "question": "Will mole removal leave a scar?",
                    "answer": "The likelihood of scarring depends on the removal method and the size of the mole. Techniques like laser removal generally leave minimal or no scarring, while excision may leave a small scar that fades over time."
                },
                {
                    "question": "Can removed moles grow back?",
                    "answer": "In rare cases, moles can regrow if the removal was incomplete. This is more likely with methods like shaving or laser treatment. If this occurs, a follow-up visit to the dermatologist is advised."
                },
                {
                    "question": "How long does it take to heal after mole removal?",
                    "answer": "Healing time can range from a few days to a couple of weeks, depending on the removal method and the size of the mole. Your doctor will provide specific aftercare instructions to promote faster healing."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Mole-Removal-Dubais-scaled.jpg",
            "beforeAfter": [
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Mole-Removal-Dubaiss-1.jpg',
                'https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Mole-Removal-Dubaiss-2.jpg',
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Mole-Removal-Dubaiss-3.jpg"
            ]
        },
        "product": {
            "id": 110,
            "title": "Mole Removal",
            "price": 490,
            "description": "Get expert mole removal in Dubai with advanced, safe treatments. Achieve smooth, blemish-free skin under the care of our certified dermatologists."
        }
    },

    "ar": {
        "url": "",
        "meta": {
            "title": "إزالة الشامات في دبي | عيادة إيليت بودي هوم",
            "description": "احصل على خدمة إزالة الشامات في دبي بأحدث التقنيات الآمنة. استمتع ببشرة ناعمة خالية من العيوب تحت رعاية أطباء الجلدية المعتمدين.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/mole-removal-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "إزالة الشامات في دبي | عيادة إيليت بودي هوم",
                "description": "احصل على خدمة إزالة الشامات في دبي بأحدث التقنيات الآمنة. استمتع ببشرة ناعمة خالية من العيوب تحت رعاية أطباء الجلدية المعتمدين.",
                "url": "https://elitebodyhome.com/services/mole-removal-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-02-13T14:23:21+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dgffhtf-scaled-large.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dgffhtf-scaled-large.jpg",
                "image_width": 1024,
                "image_height": 572,
                "image_alt": "إزالة الشامات",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "إزالة الشامات في دبي | عيادة إيليت بودي هوم",
                "description": "احصل على خدمة إزالة الشامات في دبي بأحدث التقنيات الآمنة. استمتع ببشرة ناعمة خالية من العيوب تحت رعاية أطباء الجلدية المعتمدين.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dgffhtf-scaled-large.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "إزالة الشامات في دبي"],
        "hero": {
            "title": "إزالة الشامات في دبي",
            "subtitle": "بشرة خالية من العيوب مع خبراء إزالة الشامات",
            "description": "تُعد الشامات نوع شائع من نمو الجلد، وقد تظهر بأشكال وأحجام وألوان مختلفة. وفي حين أن معظم الشامات تكون حميدة وغير ضارة، إلا أن البعض يُفضل إزالتها لأسباب جمالية أو في حال كانت تُسبب إزعاجًا. كما يُنصح بإزالة الشامة في حال الاشتباه بسرطان الجلد أو عند ملاحظة تغيرات في صفاتها. ومهما كان السبب، فيعد إجراء إزالة الشامة إجراءً بسيطًا بشكل عام، وقد يُحقق نتائج تجميلية ويمنحك راحة البال في الوقت ذاته."
        },
        "howItWorks": {
            "title": "الإجراء",
            "description": "تُعد إزالة الشامات إجراءً آمنًا، يُجرى لأسباب تجميلية أو طبية، سواء للتخلص من شامات غير مرغوب بها أو لفحص الشامات المشتبه بها."
        },
        "procedureSteps": {
            "heading": "إليك خطوات الإجراء:",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة والفحص",
                    "desc": "يقوم طبيب الجلدية بفحص الشامة لتحديد ما إذا كانت بحاجة إلى الإزالة لأسباب جمالية أو بسبب وجود مخاطر صحية مثل احتمال الإصابة بسرطان الجلد.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "التخدير الموضعي",
                    "desc": "يُستخدم مخدر موضعي لتخدير المنطقة المحيطة بالشامة، مما يضمن إجراء بدون ألم.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "الاستئصال أو الحلاقة",
                    "desc": "تُحدد الطريقة المناسبة إما الاستئصال أو الحلاقة حسب نوع الشامة وحجمها وعمقها في الجلد.",
                    "icon": "fa-solid fa-cut"
                },
                {
                    "title": "الكي والإغلاق",
                    "desc": "تُغلق الأنسجة إما بالكي أو بالغرز حسب الحالة، لضمان التئام وتقليل احتمالية التندب أو العدوى.",
                    "icon": "fa-solid fa-burn"
                },
                {
                    "title": "المتابعة والعناية بعد الإجراء",
                    "desc": "سيُبلغك الطبيب بتعليمات العناية بعد الإجراء، والتي قد تشمل تنظيف المنطقة بانتظام، وتجنب تعرضها لأشعة الشمس، واستخدام الأدوية الموصى بها.",
                    "icon": "fa-solid fa-bandage"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "قد يكون إجراء إزالة الشامات ذو فوائد تجميلية أو طبية، حسب السبب الذي يدفعك للتخلص منها. إليك نظرة مفصلة على أبرز المزايا:",
            "items": [
                {
                    "title": "تحسين المظهر الجمالي",
                    "desc": "يحفز تجديد الكولاجين في الطبقات العميقة من الجلد، مما يعزز مرونة الجلد ويمنحه نعومة أكثر.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "الوقاية من التهيج الجلدي",
                    "desc": "قد تتسبب الشامات الموجودة في مناطق الاحتكاك مثل تحت الملابس أو بالقرب من الإكسسوارات في التهاب الجلد. يساعد إزالتها على تجنب هذا النوع من المشكلات.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "الكشف المبكر عن السرطان",
                    "desc": "يمكن للفحوصات المخبرية أن تكشف عن مؤشرات مبكرة لسرطان الجلد مثل الميلانوما، وذلك من خلال تحليل الشامات المُزالة المشتبه بها.",
                    "icon": "fa-solid fa-magnifying-glass"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "قد تؤثر الشامات الكبيرة أو المتعددة على ثقة الشخص بنفسه، خاصةً إذا كانت في أماكن ظاهرة. يساعد إزالتها على تعزيز الثقة بالنفس والرضا عن الشكل العام.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "لا يناسب إجراء إزالة الشامات للجميع، لذا يجب في الخطوة الأولى التأكد من مدى أهليتك لهذا الإجراء.",
            "items": [
                {
                    "title": "الأشخاص الذين لديهم شامات غير طبيعية",
                    "desc": "يجب مراجعة طبيب الجلدية إذا لاحظت أن الشامة لها حواف غير منتظمة، أو تغير في الحجم، أو تباين في اللون. قد تكون هذه التغيرات مؤشرات مبكرة على وجود سرطان جلدي، وكلما تم التعامل معها مبكرًا، كانت النتائج أفضل.",
                    "icon": "fa-solid fa-circle-exclamation"
                },
                {
                    "title": "الأشخاص الذين يشعرون بالانزعاج أو يعانون من التهيج الجلدي",
                    "desc": "إذا كانت الشامة تعيقك أثناء الحلاقة أو تتعرض للاحتكاك المتكرر الذي يؤدي إلى التهابها، يمكن إزالتها بأمان للتخلص من الإزعاج المستمر.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "المرشحون لأسباب تجميلية",
                    "desc": "إذا كنت غير راضٍ عن مظهر الشامة لأسباب جمالية، فقد ترغب في إزالتها لتحسين مظهر بشرتك.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "الأشخاص الذين يتمتعون بصحة عامة جيدة",
                    "desc": "إذا كنت لا تعاني من حالات صحية قد تؤثر على عملية التعافي، فسيكون الإجراء آمن لك.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "كلما كنت أكثر استعدادًا للعلاج، كلما كان الإجراء أكثر سلاسة والنتائج أفضل.",
            "items": [
                {
                    "title": "زيارة طبيب الجلدية",
                    "desc": "يجب حجز استشارة مع طبيب جلدية متخصص في الخطوة الأولى عند التفكير في إزالة الشامة؛ حيث يمكنه تحديد أفضل طريقة للإزالة بناءً على خصائص الشامة ونوع البشرة.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "تجنب التعرض للشمس",
                    "desc": "يصعب إزالة الشامة ويطول الشفاء إذا كانت البشرة متضررة من الشمس، لذلك يُنصح بحماية المنطقة وعدم تعريضها لأشعة الشمس المباشرة في الأيام التي تسبق الإجراء.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "التوقف عن تناول مميعات الدم",
                    "desc": "قد يُطلب من الأشخاص الذين يتناولون أدوية مُميعة للدم، مثل الأسبرين، التوقف عنها مؤقتًا قبل الإجراء، لتقليل خطر النزيف أثناء الإجراء.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "معرفة إرشادات العناية بعد العلاج",
                    "desc": "يُساهم الالتزام بالتعليمات الخاصة بالعناية بعد الإجراء في تسريع الشفاء، ويقلل من احتمالية حدوث العدوى أو الالتهابات.",
                    "icon": "fa-solid fa-book-medical"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "تكون نتائج إزالة الشامات عادةً دائمة، خاصة إذا تم استئصال الشامة بالكامل. ورغم أن الجلد يصبح ناعمًا وخالي من الشامات، إلا أن ظهور ندبة خفيفة قد يكون أمرًا واردًا في بعض الحالات.",
            "items": [
                {
                    "title": "نتائج فورية",
                    "desc": "تختفي الشامة مباشرةً بعد الإجراء، وإذا تم استخدام الغرز، فسيتم إزالتها عادةً خلال أسبوع إلى أسبوعين.",
                    "icon": "fa-solid fa-check"
                },
                {
                    "title": "الندبات",
                    "desc": "يُحتمل ظهور ندبة بسيطة خصوصًا في حالة الاستئصال الجراحي، لكنها غالبًا ما تختفي مع الوقت. ويمكن استخدام كريمات مخصصة لتخفيف مظهر الندبة، كما يُنصح بالحفاظ على ترطيب المنطقة جيدًا.",
                    "icon": "fa-solid fa-scissors"
                },
                {
                    "title": "المتابعة",
                    "desc": "تُرسل العينة إلى المختبر لتحليلها في حال كانت الشامة مثيرة للشك. وإذا تم اكتشاف خلايا غير طبيعية، سيقوم الطبيب بشرح الخطوات القادمة في خطة العلاج.",
                    "icon": "fa-solid fa-microscope"
                }
            ]
        },

        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل إزالة الشامات مؤلمة؟",
                    "answer": "لا، عادةً ما يتم إجراء إزالة الشامات تحت التخدير الموضعي، مما يضمن عدم الشعور بألم أثناء الإجراء. أما الألم بعد الإجراء فيكون خفيفًا ويمكن التحكم فيه باستخدام مسكنات الألم المتاحة دون وصفة طبية."
                },
                {
                    "question": "هل تترك إزالة الشامات ندبة؟",
                    "answer": "يعتمد ظهور الندبة على طريقة الإزالة وحجم الشامة. عادةً ما تترك الإزالة بالليزر ندوبًا بسيطة أو لا تترك أي ندوب، بينما قد تترك الإزالة الجراحية ندبة صغيرة تختفي مع مرور الوقت."
                },
                {
                    "question": "هل يمكن أن تعود الشامات بعد إزالتها؟",
                    "answer": "في حالات نادرة، قد تعود الشامات إذا لم تتم إزالتها بالكامل. وهذا أكثر شيوعًا مع طرق مثل الحلاقة أو العلاج بالليزر. إذا حدث ذلك، يُنصح بزيارة طبيب الجلدية للمتابعة."
                },
                {
                    "question": "كم تستغرق فترة الشفاء بعد إزالة الشامة؟",
                    "answer": "قد تتراوح فترة الشفاء من بضعة أيام إلى أسبوعين، حسب طريقة الإزالة وحجم الشامة. سيقدم لك الطبيب تعليمات محددة للعناية بالمنطقة لتعزيز الشفاء السريع."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }

};
